import { createReducer } from 'redux-act';
import * as actions from '../actions/interface';
import {
  ActiveTagConstants,
  ClientConstants,
  ClientModel,
  CurrencyConstants,
  IndexedDBCache,
} from 'finhelper';
import moment from 'moment';
import CacheHelper from '#app/helpers/Cache/CacheHelper';
import { isManager } from '#app/helpers/isManager';
import SpendConstants from '#app/helpers/SpendConstants';
import IncomeConstants from '#app/helpers/IncomeConstants';
import type { InterfaceClient, ClientsApiResponse, ItemType } from '#app/types/interface';
import { PLAN_PROFILE_FIELDS } from '#app/constants/planProfileFields';

export type { InterfaceClient } from '#app/types/interface';

export interface InterfaceState {
  client?: InterfaceClient;
  propertyTypes: ItemType[];
  investTypes: ItemType[];
  [key: string]: unknown | ItemType[] | null;
}

const state: InterfaceState = {
  title: '',
  loader: false,
  clients: [],
  client: null,
  clientLoading: true,

  currency: null,
  currencyLoading: true,
  currencies: [],

  courses: [],
  coursesLoading: true,

  sidebarOpen: (() => {
    const raw = localStorage.getItem('sidebar');
    return raw !== null ? JSON.parse(raw) : true;
  })(),

  groupTypes: [],
  groupTypesLoading: true,

  classTypes: [],
  classTypesLoading: true,

  investTypes: [],
  investTypesLoading: true,

  propertyTypes: [],
  propertyTypesLoading: true,

  insuranceTypes: [],
  insuranceTypesLoading: true,

  insuranceCompanies: [],
  insuranceCompaniesLoading: true,

  incomeTypes: [],
  incomeTypesLoading: true,

  spendTypes: [],
  spendTypesLoading: true,

  obligationTypes: [],
  obligationLoading: true,

  incomeTags: [],
  spendTags: [],
  investTags: [],
  tags: [],
  tagsLoading: true,

  incomeSubcategories: [],
  spendSubcategories: [],
  subcategoriesLoading: true,

  notifications: [],
  notificationsLoading: true,
  balanceHash: '',

  /** Сброс в WebSocket при обновлении уведомлений — тогда модалка снова покажется при новых непрочитанных. При перезагрузке страницы = false. */
  hasShownUnreadNotificationsModal: false,

  /** Глобальная модалка детального просмотра уведомления: { type, id, item } или null. */
  activeNotificationForModal: null,

  atonFiles: [],
  isNotVisibleSum: false,
};

const handleAtonFiles = (state: InterfaceState, response: { data?: unknown }) => {
  return {
    ...state,
    atonFiles: response.data,
  };
};

const handleTitle = (state: InterfaceState, title: string) => {
  document.title = title;
  return {
    ...state,
    title: title,
  };
};

const handleLoader = (state: InterfaceState, bool: boolean) => {
  return {
    ...state,
    loader: bool,
  };
};

const handleUserNotifications = (
  state: InterfaceState,
  response: { result?: string; data?: unknown[] }
) => {
  if (typeof response === 'object' && response.result === 'success') {
    const notifications = response.data || [];

    return {
      ...state,
      notifications: notifications,
      notificationsLoading: false,
    };
  } else {
    return state;
  }
};

const setHasShownUnreadNotificationsModal = (state: InterfaceState, value: unknown) => ({
  ...state,
  hasShownUnreadNotificationsModal: !!value,
});

const setActiveNotificationForModal = (state: InterfaceState, payload: unknown) => ({
  ...state,
  activeNotificationForModal: payload ?? null,
});

const handleClient = (
  state: InterfaceState,
  passedData: { client: Record<string, unknown>; data: Record<string, unknown> }
) => {
  const { client, data } = passedData;

  localStorage.setItem('client', JSON.stringify(client));

  return {
    ...state,
    client: client,
    clientLoading: false,
    isNotVisibleSum: isManager(data) && client.is_visible_spend === false,
  };
};

const handleCurrency = (state: InterfaceState, currency: unknown) => {
  localStorage.setItem('currency', JSON.stringify(currency));

  return {
    ...state,
    currency: currency,
    currencyLoading: false,
  };
};

const handleClients = (state: InterfaceState, response: ClientsApiResponse) => {
  if (typeof response === 'object' && response.result === 'success') {
    return {
      ...state,
      clients: ClientModel.load((response.data ?? []) as Record<string, unknown>[]),
    };
  } else {
    return state;
  }
};

const handleSearchClients = (state: InterfaceState, response: ClientsApiResponse) => {
  if (typeof response === 'object' && response.result === 'success') {
    return {
      ...state,
      clients: ClientModel.load((response.data ?? []) as Record<string, unknown>[]),
    };
  } else {
    return state;
  }
};

const handleCurrentClient = (state: InterfaceState, client: Record<string, unknown> | null) => {
  if (client) {
    switch (client.role) {
      case ClientConstants.CLIENT: {
        // Удаляем поле "depositTypes" чтобы не держать справочник в Redux.
        // Раньше оно использовалось в формах, но теперь берётся отдельным query.
        const { depositTypes: _depositTypes, ...rest } = state as any;
        void _depositTypes;

        let data: InterfaceState = {
          ...rest,
          client: client,
          clientLoading: false,
        };

        if (typeof client.get_property_types !== 'undefined') {
          data.propertyTypes = client.get_property_types as ItemType[];
          data.propertyTypesLoading = false;
        }
        if (typeof client.get_insurance_types !== 'undefined') {
          data.insuranceTypes = client.get_insurance_types;
          data.insuranceTypesLoading = false;
        }
        if (typeof client.get_group_types !== 'undefined') {
          data.groupTypes = client.get_group_types;
          data.groupTypesLoading = false;
        }
        if (typeof client.get_class_types !== 'undefined') {
          data.classTypes = client.get_class_types;
          data.classTypesLoading = false;
        }
        if (typeof client.get_class_types !== 'undefined') {
          data.classTypes = client.get_class_types;
          data.classTypesLoading = false;
        }
        if (typeof client.get_income_types !== 'undefined') {
          data.incomeTypes = client.get_income_types;
          data.incomeTypesLoading = false;

          IncomeConstants.data = client.get_income_types as typeof IncomeConstants.data;
        }
        if (typeof client.get_invest_types !== 'undefined') {
          data.investTypes = client.get_invest_types as ItemType[];
          data.investTypesLoading = false;
        }
        if (typeof client.get_spending_types !== 'undefined') {
          data.spendTypes = client.get_spending_types;
          data.spendTypesLoading = false;

          SpendConstants.data = client.get_spending_types as typeof SpendConstants.data;
        }
        if (typeof client.get_obligation_types !== 'undefined') {
          data.obligationTypes = client.get_obligation_types;
          data.obligationLoading = false;
        }

        return data;
      }
      case ClientConstants.MANAGER:
      case ClientConstants.OWNER:
      case ClientConstants.DEPARTMENT_BOSS:
      case ClientConstants.DIRECTOR:
        break;
    }
  }

  return {
    ...state,
  };
};
const handleClearClient = (state: InterfaceState, _client: unknown) => {
  localStorage.removeItem('client');

  const { depositTypes: _depositTypes, ...rest } = state as any;
  void _depositTypes;

  return {
    ...rest,
    client: null,
    clientLoading: true,
  };
};

const handleCurrencies = (state: InterfaceState, data: unknown[]) => {
  const rawCurrency = localStorage.getItem('currency');
  let currency: unknown = rawCurrency !== null ? JSON.parse(rawCurrency) : (data[0] ?? null);

  CurrencyConstants.currencies = data as typeof CurrencyConstants.currencies;

  return {
    ...state,
    currency,
    currencyLoading: !currency,
    currencies: data,
  } as InterfaceState;
};

const handleCourses = (state: InterfaceState, data: unknown) => {
  CurrencyConstants.courses = data as typeof CurrencyConstants.courses;

  return {
    ...state,
    courses: data as unknown[],
    coursesLoading: false,
  };
};

const handleSpendTypes = (state: InterfaceState, response: { result?: string; data?: unknown }) => {
  if (typeof response === 'object' && response.result === 'success') {
    return {
      ...state,
      spendTypes: response.data,
      spendTypesLoading: false,
    };
  } else {
    return state;
  }
};
const handleInsuranceCompanies = (
  state: InterfaceState,
  response: { result?: string; data?: unknown }
) => {
  if (typeof response === 'object' && response.result === 'success') {
    return {
      ...state,
      insuranceCompanies: response.data,
      insuranceCompaniesLoading: false,
    };
  } else {
    return state;
  }
};

const handleIncomeTypes = (
  state: InterfaceState,
  response: { result?: string; data?: unknown }
) => {
  if (typeof response === 'object' && response.result === 'success') {
    return {
      ...state,
      incomeTypes: response.data,
      incomeTypesLoading: false,
    };
  } else {
    return state;
  }
};

const handleSidebarToggle = (state: InterfaceState) => {
  localStorage.setItem('sidebar', JSON.stringify(!state.sidebarOpen));

  return {
    ...state,
    sidebarOpen: !state.sidebarOpen,
  };
};

const handleSidebarOpen = (state: InterfaceState) => {
  localStorage.setItem('sidebar', JSON.stringify(true));

  return {
    ...state,
    sidebarOpen: true,
  };
};

const handleActiveTags = (
  state: InterfaceState,
  response: { result?: string; data?: unknown[] }
) => {
  if (typeof response === 'object' && response.result === 'success') {
    const incomeTags: unknown[] = [];
    const investTags: unknown[] = [];
    const spendTags: unknown[] = [];
    const tags: unknown[] = [];

    if (Array.isArray(response.data)) {
      response.data.forEach((item: unknown) => {
        const tag = item as Record<string, unknown>;
        tags.push(item);

        if (tag.type_id === ActiveTagConstants.INCOME) {
          incomeTags.push(item);
        } else if (tag.type_id === ActiveTagConstants.SPEND) {
          spendTags.push(item);
        } else if (tag.type_id === ActiveTagConstants.INVEST) {
          investTags.push(item);
        }
      });
    }

    return {
      ...state,
      tags: tags,
      incomeTags: incomeTags,
      spendTags: spendTags,
      investTags: investTags,
      tagsLoading: false,
    };
  } else {
    return state;
  }
};

const handleActiveSubcategories = (
  state: InterfaceState,
  response: { result?: string; data?: unknown[] }
) => {
  if (typeof response === 'object' && response.result === 'success') {
    const incomeSubcategories: unknown[] = [];
    const spendSubcategories: unknown[] = [];

    if (Array.isArray(response.data)) {
      response?.data?.forEach((item: unknown) => {
        const sub = item as Record<string, unknown>;
        if (sub.type_id === ActiveTagConstants.INCOME) {
          incomeSubcategories.push(item);
        } else if (sub.type_id === ActiveTagConstants.SPEND) {
          spendSubcategories.push(item);
        }
      });
    }

    return {
      ...state,
      incomeSubcategories: incomeSubcategories,
      spendSubcategories: spendSubcategories,
      subcategoriesLoading: false,
    };
  } else {
    return state;
  }
};

const handlePropertyTypes = (
  state: InterfaceState,
  response: { result?: string; data?: unknown }
) => {
  if (typeof response === 'object' && response.result === 'success') {
    return {
      ...state,
      propertyTypes: response.data,
      propertyTypesLoading: false,
    };
  } else {
    return state;
  }
};

const handleInvestTypes = (
  state: InterfaceState,
  response: { result?: string; data?: unknown }
) => {
  if (typeof response === 'object' && response.result === 'success') {
    return {
      ...state,
      investTypes: response.data,
      investTypesLoading: false,
    };
  } else {
    return state;
  }
};

const handleGroupTypes = (state: InterfaceState, response: { result?: string; data?: unknown }) => {
  if (typeof response === 'object' && response.result === 'success') {
    return {
      ...state,
      groupTypes: response.data,
      groupTypesLoading: false,
    };
  } else {
    return state;
  }
};

const handleClassTypes = (state: InterfaceState, response: { result?: string; data?: unknown }) => {
  if (typeof response === 'object' && response.result === 'success') {
    return {
      ...state,
      classTypes: response.data,
      classTypesLoading: false,
    };
  } else {
    return state;
  }
};

const handleReloadBalance = (state: InterfaceState) => {
  const client = state.client as { id: number } | null;
  const currency = state.currency as { id: number } | null;
  if (client && currency) {
    const cacheKey = CacheHelper.generateKey(CacheHelper.BALANCE, {
      clientId: client.id,
      currencyId: currency.id,
    });

    IndexedDBCache.delete(cacheKey);
  }

  let now = moment();

  return {
    ...state,
    balanceHash: now.format('YYYY-MM-DD HH:mm:ss'),
  };
};

const handleUpdatePlanProfileFields = (
  state: InterfaceState,
  payload: Partial<Record<(typeof PLAN_PROFILE_FIELDS)[number], unknown>>
) => {
  if (!state.client) return state;
  return {
    ...state,
    client: {
      ...(state.client as Record<string, unknown>),
      ...payload,
    } as InterfaceClient,
  };
};

const reducer = createReducer<InterfaceState>(
  {
    [actions.handleTitle as any]: handleTitle,
    [actions.handleAtonFiles as any]: handleAtonFiles,
    [actions.handleCurrency as any]: handleCurrency,
    [actions.handleClient as any]: handleClient,
    [actions.handleClients as any]: handleClients,
    [actions.handleSearchClients as any]: handleSearchClients,
    [actions.handleCurrentClient as any]: handleCurrentClient,
    [actions.handleCurrencies as any]: handleCurrencies,
    [actions.handleCourses as any]: handleCourses,
    [actions.handleClearClient as any]: handleClearClient,
    [actions.handleLoader as any]: handleLoader,
    [actions.handleSidebarToggle as any]: handleSidebarToggle,
    [actions.handleSidebarOpen as any]: handleSidebarOpen,
    [actions.handleSpendTypes as any]: handleSpendTypes,
    [actions.handleIncomeTypes as any]: handleIncomeTypes,
    [actions.handleActiveTags as any]: handleActiveTags,
    [actions.handleActiveSubcategories as any]: handleActiveSubcategories,
    [actions.handlePropertyTypes as any]: handlePropertyTypes,
    [actions.handleInvestTypes as any]: handleInvestTypes,
    [actions.handleGroupTypes as any]: handleGroupTypes,
    [actions.handleClassTypes as any]: handleClassTypes,
    [actions.handleUserNotifications as any]: handleUserNotifications,
    [actions.setHasShownUnreadNotificationsModal as any]: setHasShownUnreadNotificationsModal,
    [actions.setActiveNotificationForModal as any]: setActiveNotificationForModal,
    [actions.handleInsuranceCompanies as any]: handleInsuranceCompanies,
    [actions.handleReloadBalance as any]: handleReloadBalance,
    [actions.handleUpdatePlanProfileFields as any]: handleUpdatePlanProfileFields,
  } as any,
  state
);

export default reducer;

///
// import { createReducer } from 'redux-act';
// import * as actions from '../actions/interface';
// import {
//   ActiveTagConstants,
//   ClientConstants,
//   ClientModel,
//   CurrencyConstants,
//   IndexedDBCache,
// } from 'finhelper';
// import moment from 'moment';
// import CacheHelper from '#app/helpers/Cache/CacheHelper';
// import { isManager } from '#app/helpers/isManager';
// import SpendConstants from '#app/helpers/SpendConstants';
// import IncomeConstants from '#app/helpers/IncomeConstants';

// let state = {
//   title: '',
//   loader: false,
//   clients: [],
//   client: null,
//   clientLoading: true,

//   currency: null,
//   currencyLoading: true,
//   currencies: [],

//   courses: [],
//   coursesLoading: true,

//   sidebarOpen: localStorage.getItem('sidebar') ? JSON.parse(localStorage.getItem('sidebar')) : true,

//   groupTypes: [],
//   groupTypesLoading: true,

//   classTypes: [],
//   classTypesLoading: true,

//   investTypes: [],
//   investTypesLoading: true,

//   propertyTypes: [],
//   propertyTypesLoading: true,

//   insuranceTypes: [],
//   insuranceTypesLoading: true,

//   insuranceCompanies: [],
//   insuranceCompaniesLoading: true,

//   incomeTypes: [],
//   incomeTypesLoading: true,

//   spendTypes: [],
//   spendTypesLoading: true,

//   obligationTypes: [],
//   obligationLoading: true,

//   incomeTags: [],
//   spendTags: [],
//   investTags: [],
//   tags: [],
//   tagsLoading: true,

//   incomeSubcategories: [],
//   spendSubcategories: [],
//   subcategoriesLoading: true,

//   notifications: [],
//   notificationsLoading: true,
//   balanceHash: '',

//   /** Сброс в WebSocket при обновлении уведомлений — тогда модалка снова покажется при новых непрочитанных. При перезагрузке страницы = false. */
//   hasShownUnreadNotificationsModal: false,

//   /** Глобальная модалка детального просмотра уведомления: { type, id, item } или null. */
//   activeNotificationForModal: null,

//   atonFiles: [],
//   isNotVisibleSum: false,
// };

// const handleAtonFiles = (state, response) => {
//   return {
//     ...state,
//     atonFiles: response.data,
//   };
// };

// const handleTitle = (state, title) => {
//   document.title = title;
//   return {
//     ...state,
//     title: title,
//   };
// };

// const handleLoader = (state, bool) => {
//   return {
//     ...state,
//     loader: bool,
//   };
// };

// const handleUserNotifications = (state, response) => {
//   if (typeof response === 'object' && response.result === 'success') {
//     const notifications = response.data || [];

//     return {
//       ...state,
//       notifications: notifications,
//       notificationsLoading: false,
//     };
//   } else {
//     return state;
//   }
// };

// const setHasShownUnreadNotificationsModal = (state, value) => ({
//   ...state,
//   hasShownUnreadNotificationsModal: !!value,
// });

// const setActiveNotificationForModal = (state, payload) => ({
//   ...state,
//   activeNotificationForModal: payload ?? null,
// });

// const handleClient = (state, passedData) => {
//   const { client, data } = passedData;

//   localStorage.setItem('client', JSON.stringify(client));

//   return {
//     ...state,
//     client: client,
//     clientLoading: false,
//     isNotVisibleSum: isManager(data) && client.is_visible_spend === false,
//   };
// };

// const handleCurrency = (state, currency) => {
//   localStorage.setItem('currency', JSON.stringify(currency));

//   return {
//     ...state,
//     currency: currency,
//     currencyLoading: false,
//   };
// };

// const handleClients = (state, response) => {
//   if (typeof response === 'object' && response.result === 'success') {
//     return {
//       ...state,
//       clients: ClientModel.load(response.data as Record<string, unknown>[]),
//     };
//   } else {
//     return state;
//   }
// };

// const handleSearchClients = (state, response) => {
//   if (typeof response === 'object' && response.result === 'success') {
//     return {
//       ...state,
//       clients: ClientModel.load(response.data as Record<string, unknown>[]),
//     };
//   } else {
//     return state;
//   }
// };

// const handleCurrentClient = (state, client) => {
//   if (client) {
//     switch (client.role) {
//       case ClientConstants.CLIENT: {
//         let data = {
//           ...state,
//           client: client,
//           clientLoading: false,
//         };

//         if (typeof client.get_property_types !== 'undefined') {
//           data.propertyTypes = client.get_property_types;
//           data.propertyTypesLoading = false;
//         }
//         if (typeof client.get_insurance_types !== 'undefined') {
//           data.insuranceTypes = client.get_insurance_types;
//           data.insuranceTypesLoading = false;
//         }
//         if (typeof client.get_group_types !== 'undefined') {
//           data.groupTypes = client.get_group_types;
//           data.groupTypesLoading = false;
//         }
//         if (typeof client.get_class_types !== 'undefined') {
//           data.classTypes = client.get_class_types;
//           data.classTypesLoading = false;
//         }
//         if (typeof client.get_class_types !== 'undefined') {
//           data.classTypes = client.get_class_types;
//           data.classTypesLoading = false;
//         }
//         if (typeof client.get_income_types !== 'undefined') {
//           data.incomeTypes = client.get_income_types;
//           data.incomeTypesLoading = false;

//           IncomeConstants.data = client.get_income_types;
//         }
//         if (typeof client.get_invest_types !== 'undefined') {
//           data.investTypes = client.get_invest_types;
//           data.investTypesLoading = false;
//         }
//         if (typeof client.get_spending_types !== 'undefined') {
//           data.spendTypes = client.get_spending_types;
//           data.spendTypesLoading = false;

//           SpendConstants.data = client.get_spending_types;
//         }
//         if (typeof client.get_obligation_types !== 'undefined') {
//           data.obligationTypes = client.get_obligation_types;
//           data.obligationLoading = false;
//         }

//         return data;
//       }
//       case ClientConstants.MANAGER:
//       case ClientConstants.OWNER:
//       case ClientConstants.DEPARTMENT_BOSS:
//       case ClientConstants.DIRECTOR:
//         break;
//     }
//   }

//   return {
//     ...state,
//   };
// };
// const handleClearClient = (state, _client) => {
//   localStorage.removeItem('client');

//   return {
//     ...state,
//     client: null,
//     clientLoading: true,
//   };
// };

// const handleCurrencies = (state, data) => {
//   let currency = localStorage.getItem('currency')
//     ? JSON.parse(localStorage.getItem('currency'))
//     : data[0]
//       ? data[0]
//       : null;

//   CurrencyConstants.currencies = data;

//   return {
//     ...state,
//     currency: currency,
//     currencyLoading: !currency,
//     currencies: data,
//   };
// };

// const handleCourses = (state, data) => {
//   CurrencyConstants.courses = data;

//   return {
//     ...state,
//     courses: data,
//     coursesLoading: false,
//   };
// };

// const handleSpendTypes = (state, response) => {
//   if (typeof response === 'object' && response.result === 'success') {
//     return {
//       ...state,
//       spendTypes: response.data,
//       spendTypesLoading: false,
//     };
//   } else {
//     return state;
//   }
// };
// const handleInsuranceCompanies = (state, response) => {
//   if (typeof response === 'object' && response.result === 'success') {
//     return {
//       ...state,
//       insuranceCompanies: response.data,
//       insuranceCompaniesLoading: false,
//     };
//   } else {
//     return state;
//   }
// };

// const handleIncomeTypes = (state, response) => {
//   if (typeof response === 'object' && response.result === 'success') {
//     return {
//       ...state,
//       incomeTypes: response.data,
//       incomeTypesLoading: false,
//     };
//   } else {
//     return state;
//   }
// };

// const handleSidebarToggle = (state) => {
//   localStorage.setItem('sidebar', JSON.stringify(!state.sidebarOpen));

//   return {
//     ...state,
//     sidebarOpen: !state.sidebarOpen,
//   };
// };

// const handleSidebarOpen = (state) => {
//   localStorage.setItem('sidebar', JSON.stringify(true));

//   return {
//     ...state,
//     sidebarOpen: true,
//   };
// };

// const handleActiveTags = (state, response) => {
//   if (typeof response === 'object' && response.result === 'success') {
//     let incomeTags = [],
//       investTags = [],
//       spendTags = [],
//       tags = [];

//     if (Array.isArray(response.data)) {
//       response.data.forEach((item) => {
//         tags.push(item);

//         if (item.type_id === ActiveTagConstants.INCOME) {
//           incomeTags.push(item);
//         } else if (item.type_id === ActiveTagConstants.SPEND) {
//           spendTags.push(item);
//         } else if (item.type_id === ActiveTagConstants.INVEST) {
//           investTags.push(item);
//         }
//       });
//     }

//     return {
//       ...state,
//       tags: tags,
//       incomeTags: incomeTags,
//       spendTags: spendTags,
//       investTags: investTags,
//       tagsLoading: false,
//     };
//   } else {
//     return state;
//   }
// };

// const handleActiveSubcategories = (state, response) => {
//   if (typeof response === 'object' && response.result === 'success') {
//     let incomeSubcategories = [],
//       spendSubcategories = [];

//     if (Array.isArray(response.data)) {
//       response?.data?.forEach((item) => {
//         if (item.type_id === ActiveTagConstants.INCOME) {
//           incomeSubcategories.push(item);
//         } else if (item.type_id === ActiveTagConstants.SPEND) {
//           spendSubcategories.push(item);
//         }
//       });
//     }

//     return {
//       ...state,
//       incomeSubcategories: incomeSubcategories,
//       spendSubcategories: spendSubcategories,
//       subcategoriesLoading: false,
//     };
//   } else {
//     return state;
//   }
// };

// const handlePropertyTypes = (state, response) => {
//   if (typeof response === 'object' && response.result === 'success') {
//     return {
//       ...state,
//       propertyTypes: response.data,
//       propertyTypesLoading: false,
//     };
//   } else {
//     return state;
//   }
// };

// const handleInvestTypes = (state, response) => {
//   if (typeof response === 'object' && response.result === 'success') {
//     return {
//       ...state,
//       investTypes: response.data,
//       investTypesLoading: false,
//     };
//   } else {
//     return state;
//   }
// };

// const handleGroupTypes = (state, response) => {
//   if (typeof response === 'object' && response.result === 'success') {
//     return {
//       ...state,
//       groupTypes: response.data,
//       groupTypesLoading: false,
//     };
//   } else {
//     return state;
//   }
// };

// const handleClassTypes = (state, response) => {
//   if (typeof response === 'object' && response.result === 'success') {
//     return {
//       ...state,
//       classTypes: response.data,
//       classTypesLoading: false,
//     };
//   } else {
//     return state;
//   }
// };

// const handleReloadBalance = (state) => {
//   if (state.client && state.currency) {
//     let cacheKey = CacheHelper.generateKey(CacheHelper.BALANCE, {
//       clientId: state.client.id,
//       currencyId: state.currency.id,
//     });

//     IndexedDBCache.delete(cacheKey);
//   }

//   let now = moment();

//   return {
//     ...state,
//     balanceHash: now.format('YYYY-MM-DD HH:mm:ss'),
//   };
// };

// const reducer = createReducer(
//   {
//     [actions.handleTitle]: handleTitle,
//     [actions.handleAtonFiles]: handleAtonFiles,
//     [actions.handleCurrency]: handleCurrency,
//     [actions.handleClient]: handleClient,
//     [actions.handleClients]: handleClients,
//     [actions.handleSearchClients]: handleSearchClients,
//     [actions.handleCurrentClient]: handleCurrentClient,
//     [actions.handleCurrencies]: handleCurrencies,
//     [actions.handleCourses]: handleCourses,
//     [actions.handleClearClient]: handleClearClient,
//     [actions.handleLoader]: handleLoader,
//     [actions.handleSidebarToggle]: handleSidebarToggle,
//     [actions.handleSidebarOpen]: handleSidebarOpen,
//     [actions.handleSpendTypes]: handleSpendTypes,
//     [actions.handleIncomeTypes]: handleIncomeTypes,
//     [actions.handleActiveTags]: handleActiveTags,
//     [actions.handleActiveSubcategories]: handleActiveSubcategories,
//     [actions.handlePropertyTypes]: handlePropertyTypes,
//     [actions.handleInvestTypes]: handleInvestTypes,
//     [actions.handleGroupTypes]: handleGroupTypes,
//     [actions.handleClassTypes]: handleClassTypes,
//     [actions.handleUserNotifications]: handleUserNotifications,
//     [actions.setHasShownUnreadNotificationsModal]: setHasShownUnreadNotificationsModal,
//     [actions.setActiveNotificationForModal]: setActiveNotificationForModal,
//     [actions.handleInsuranceCompanies]: handleInsuranceCompanies,
//     [actions.handleReloadBalance]: handleReloadBalance,
//   },
//   state
// );

// export default reducer;
